import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
/**
 * Copyright (c) Microblink Ltd. All rights reserved.
 */

/**
 * BlinkID In-browser SDK demo app which demonstrates how to:
 *
 * - Change default SDK settings
 * - Scan front side of the identity document with web camera
 * - Provide visual feedback to the end-user during the scan
 */
import * as BlinkIDSDK from "@microblink/blinkid-in-browser-sdk";
import { Button, Modal, Upload, message } from "antd";
import Tutorial from "../../../../components/tutorial";
import { renderKeyMicroblink } from "../../../../helper/renderKeyMicroblink";
const locale = require("react-redux-i18n").I18n;
/**
 * Check browser support, customize settings and load WASM SDK.
 */

function Microblink(props) {
  const { onKycSuccess } = props;
  const [imageSrc, setImageSrc] = useState(null);
  const [isAnother, setIsAnother] = useState(false);
  const history = useHistory();
  const progressEl = document?.getElementById("load-progress");
  const videoRecognizerRef = useRef(null);
  useEffect(() => {
    if (progressEl) {
    }
    initmicroblink();
  }, [progressEl]);
  useEffect(() => {
    return () => {
      if (videoRecognizerRef.current) {
        videoRecognizerRef.current.releaseVideoFeed();
        // ... other resource release calls if necessary
      }
      // ... additional cleanup if required
    };
  }, []);
  async function initmicroblink() {
    // General UI helpers
    const initialMessageEl = await document?.getElementById("msg");
    const progressEl = await document?.getElementById("load-progress");
    // UI elements for scanning feedback

    // Check if browser has proper support for WebAssembly
    if (!BlinkIDSDK?.isBrowserSupported()) {
      if (initialMessageEl) {
        initialMessageEl.innerText = "This browser is not supported!";
      }
      console.log("This browser is not supported!");
      return;
    }

    // 1. It's possible to obtain a free trial license key on microblink.com
    const licenseKey = renderKeyMicroblink();

    // 2. Create instance of SDK load settings with your license key
    const loadSettings = new BlinkIDSDK.WasmSDKLoadSettings(licenseKey);

    // [OPTIONAL] Change default settings

    // Show or hide hello message in browser console when WASM is successfully loaded
    loadSettings.allowHelloMessage = true;

    // In order to provide better UX, display progress bar while loading the SDK
    loadSettings.loadProgressCallback = (progress) => {
      if (progressEl) {
        progressEl.value = progress;
      }
    };

    // Set absolute location of the engine, i.e. WASM and support JS files
    loadSettings.engineLocation = window.location.origin + "/resources";

    // Set absolute location of the worker file
    loadSettings.workerLocation =
      (await window.location.origin) +
      "/resources/BlinkIDWasmSDK.worker.min.js";

    // 3. Load SDK
    BlinkIDSDK.loadWasmModule(loadSettings).then(
      (sdk) => {
        document.getElementById("screen-initial")?.classList.add("hidden");
        document.getElementById("screen-start")?.classList.remove("hidden");
        document
          .getElementById("start-scan")
          ?.addEventListener("click", (ev) => {
            ev.preventDefault();
            startScan(sdk);
          });
        document.getElementById("screen-initial")?.classList.add("hidden");
        document.getElementById("screen-start")?.classList.remove("hidden");
        document
          .getElementById("image-file")
          ?.addEventListener("change", (ev) => {
            ev.preventDefault();
            startScanFile(sdk, ev.target.files);
          });
      },
      (error) => {
        if (initialMessageEl) {
          initialMessageEl.innerText = "Failed to load";
        }
        console.error("Failed to load SDK!", error);
      }
    );
  }

  /**
   * Scan single side of identity document with web camera.
   */
  async function startScan(sdk) {
    document.getElementById("screen-start")?.classList.add("hidden");
    document.getElementById("screen-scanning")?.classList.remove("hidden");
    const cameraFeed = document?.getElementById("camera-feed");
    // 1. Create a recognizer objects which will be used to recognize single image or stream of images.
    //
    // BlinkID Single-side Recognizer - scan various ID documents
    // ID Barcode Recognizer - scan barcodes from various ID documents
    const singleSideIDRecognizer =
      await BlinkIDSDK.createBlinkIdSingleSideRecognizer(sdk);
    const setting = await singleSideIDRecognizer.currentSettings();
    setting.returnEncodedFullDocumentImage = true;
    setting.anonymizationMode = 0;
    await singleSideIDRecognizer.updateSettings(setting);
    const idBarcodeRecognizer = await BlinkIDSDK.createIdBarcodeRecognizer(sdk);

    // [OPTIONAL] Create a callbacks object that will receive recognition events, such as detected object location etc.
    const callbacks = {
      onQuadDetection: (quad) => drawQuad(quad),
      onDetectionFailed: () => updateScanFeedback(locale.t("kyc_fail"), true),
    };

    // 2. Create a RecognizerRunner object which orchestrates the recognition with one or more
    //    recognizer objects.

    const recognizerRunner = await BlinkIDSDK.createRecognizerRunner(
      // SDK instance to use
      sdk,
      // List of recognizer objects that will be associated with created RecognizerRunner object
      [singleSideIDRecognizer, idBarcodeRecognizer],
      // [OPTIONAL] Should recognition pipeline stop as soon as first recognizer in chain finished recognition
      false,
      // [OPTIONAL] Callbacks object that will receive recognition events
      callbacks
    );

    // 3. Create a VideoRecognizer object and attach it to HTMLVideoElement that will be used for displaying the camera feed
    const videoRecognizer =
      await BlinkIDSDK.VideoRecognizer.createVideoRecognizerFromCameraStream(
        cameraFeed,
        recognizerRunner
      );
    videoRecognizerRef.current = videoRecognizer;
    // 4. Start the recognition and await for the results
    const processResult = await videoRecognizer.recognize();

    // 5. If recognition was successful, obtain the result and display it

    if (processResult !== BlinkIDSDK.RecognizerResultState.Empty) {
      const singleSideIDResults = await singleSideIDRecognizer.getResult();
      if (
        singleSideIDResults.state !== BlinkIDSDK.RecognizerResultState.Empty
      ) {
        const firstName =
          singleSideIDResults.firstName.latin ||
          singleSideIDResults.firstName.cyrillic ||
          singleSideIDResults.firstName.arabic ||
          singleSideIDResults.mrz.secondaryID;

        const lastName =
          singleSideIDResults.lastName.latin ||
          singleSideIDResults.lastName.cyrillic ||
          singleSideIDResults.lastName.arabic;

        const fullName =
          singleSideIDResults.fullName.latin ||
          singleSideIDResults.fullName.cyrillic ||
          singleSideIDResults.fullName.arabic ||
          lastName ||
          `${singleSideIDResults.mrz.secondaryID} ${singleSideIDResults.mrz.primaryID}`;

        const dateOfBirth = {
          year:
            singleSideIDResults.dateOfBirth.year ||
            singleSideIDResults.mrz.dateOfBirth.year,
          month:
            singleSideIDResults.dateOfBirth.month ||
            singleSideIDResults.mrz.dateOfBirth.month,
          day:
            singleSideIDResults.dateOfBirth.day ||
            singleSideIDResults.mrz.dateOfBirth.day,
        };
        const dateOfExpiry = {
          year:
            singleSideIDResults.dateOfExpiry.year ||
            singleSideIDResults.mrz.dateOfExpiry.year,
          month:
            singleSideIDResults.dateOfExpiry.month ||
            singleSideIDResults.mrz.dateOfExpiry.month,
          day:
            singleSideIDResults.dateOfExpiry.day ||
            singleSideIDResults.mrz.dateOfExpiry.day,
        };
        const countryName = singleSideIDResults.classInfo.isoAlpha2CountryCode;
        const documentType = singleSideIDResults.classInfo.documentType;

        const derivedFullName = `${firstName} ${lastName}`.trim() || fullName;

        const blob = new Blob(
          [singleSideIDResults.fullDocumentImage.encodedImage.buffer],
          { type: "image/jpeg" }
        );
        const url = URL.createObjectURL(blob);

        setImageSrc(url);

        const binaryString = Array.from(
          singleSideIDResults.fullDocumentImage.encodedImage
        )
          .map((byte) => String.fromCharCode(byte))
          .join("");
        const base64Strings = `${btoa(binaryString)}`;
        onKycSuccess({
          first_name:
            firstName?.replace(/\n/g, " ") ||
            fullName?.replace(/\n/g, " ") ||
            derivedFullName?.replace(/\n/g, " "),
          last_name: lastName.replace(/\n/g, " ") || "",
          id_card_number:
            singleSideIDResults.documentAdditionalNumber.latin ||
            singleSideIDResults.documentNumber.latin ||
            singleSideIDResults.personalIdNumber.latin,
          birth_date: `${dateOfBirth.year}-${dateOfBirth.month}-${dateOfBirth.day}`,
          dateOfExpiry: `${dateOfExpiry.year}-${dateOfExpiry.month}-${dateOfExpiry.day}`,
          countryName: countryName,
          documentType: documentType,
          file: base64Strings,
        });
        // alert(
        //   `Hello, ${derivedFullName}!\n You were born on ${dateOfBirth.year}-${dateOfBirth.month}-${dateOfBirth.day}.`
        // );
      }

      const idBarcodeResult = await idBarcodeRecognizer.getResult();
      if (idBarcodeResult.state !== BlinkIDSDK.RecognizerResultState.Empty) {
        const firstName = idBarcodeResult.firstName;
        const lastName = idBarcodeResult.lastName;
        const dateOfBirth = {
          year: idBarcodeResult.dateOfBirth.year,
          month: idBarcodeResult.dateOfBirth.month,
          day: idBarcodeResult.dateOfBirth.day,
        };

        alert(
          `Hello, ${firstName} ${lastName}!\nYou were born on ${dateOfBirth.year}-${dateOfBirth.month}-${dateOfBirth.day}.`
        );
      }
    } else {
      message.error(locale.t("error_verify_kyc"));
      history.push(`/${localStorage.getItem("locale")}/wallet/account`);
    }

    // 7. Release all resources allocated on the WebAssembly heap and associated with camera stream

    // Release browser resources associated with the camera stream
    videoRecognizer?.releaseVideoFeed();

    // Release memory on WebAssembly heap used by the RecognizerRunner
    recognizerRunner?.delete();

    // Release memory on WebAssembly heap used by the recognizer
    singleSideIDRecognizer?.delete();
    idBarcodeRecognizer?.delete();

    // Clear any leftovers drawn to canvas
    clearDrawCanvas();

    // Hide scanning screen and show scan button again
    document.getElementById("screen-start")?.classList.remove("hidden");
    document.getElementById("screen-scanning")?.classList.add("hidden");
  }

  /**
   * Utility functions for drawing detected quadrilateral onto canvas.
   */
  function drawQuad(quad) {
    const cameraFeedback = document?.getElementById("camera-feedback");
    const drawContext = cameraFeedback?.getContext("2d");

    clearDrawCanvas();

    // Based on detection status, show appropriate color and message
    setupColor(quad);
    setupMessage(quad);

    applyTransform(quad.transformMatrix);
    if (cameraFeedback) {
      drawContext.beginPath();
      drawContext.moveTo(quad.topLeft.x, quad.topLeft.y);
      drawContext.lineTo(quad.topRight.x, quad.topRight.y);
      drawContext.lineTo(quad.bottomRight.x, quad.bottomRight.y);
      drawContext.lineTo(quad.bottomLeft.x, quad.bottomLeft.y);
      drawContext.closePath();
      drawContext.stroke();
    }
  }

  /**
   * This function will make sure that coordinate system associated with detectionResult
   * canvas will match the coordinate system of the image being recognized.
   */
  function applyTransform(transformMatrix) {
    const cameraFeedback = document?.getElementById("camera-feedback");
    const cameraFeed = document?.getElementById("camera-feed");
    if (cameraFeedback && cameraFeed) {
      const drawContext = cameraFeedback?.getContext("2d");
      const canvasAR = cameraFeedback?.width / cameraFeedback?.height;
      const videoAR = cameraFeed?.videoWidth / cameraFeed?.videoHeight;

      let xOffset = 0;
      let yOffset = 0;
      let scaledVideoHeight = 0;
      let scaledVideoWidth = 0;

      if (canvasAR > videoAR) {
        // pillarboxing: https://en.wikipedia.org/wiki/Pillarbox
        scaledVideoHeight = cameraFeedback.height;
        scaledVideoWidth = videoAR * scaledVideoHeight;
        xOffset = (cameraFeedback.width - scaledVideoWidth) / 2.0;
      } else {
        // letterboxing: https://en.wikipedia.org/wiki/Letterboxing_(filming)
        scaledVideoWidth = cameraFeedback?.width;
        scaledVideoHeight = scaledVideoWidth / videoAR;
        yOffset = (cameraFeedback.height - scaledVideoHeight) / 2.0;
      }

      // first transform canvas for offset of video preview within the HTML video element (i.e. correct letterboxing or pillarboxing)
      drawContext.translate(xOffset, yOffset);
      // second, scale the canvas to fit the scaled video
      drawContext.scale(
        scaledVideoWidth / cameraFeed?.videoWidth,
        scaledVideoHeight / cameraFeed?.videoHeight
      );

      // finally, apply transformation from image coordinate system to
      // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/setTransform
      drawContext.transform(
        transformMatrix[0],
        transformMatrix[3],
        transformMatrix[1],
        transformMatrix[4],
        transformMatrix[2],
        transformMatrix[5]
      );
    }
  }

  function clearDrawCanvas() {
    const cameraFeedback = document?.getElementById("camera-feedback");
    const cameraFeed = document?.getElementById("camera-feed");
    const drawContext = cameraFeedback?.getContext("2d");
    if (cameraFeedback) {
      cameraFeedback.width = cameraFeedback?.clientWidth;
      cameraFeedback.height = cameraFeedback?.clientHeight;
      drawContext.clearRect(
        0,
        0,
        cameraFeedback?.width,
        cameraFeedback?.height
      );
    }
  }

  function setupColor(displayable) {
    const cameraFeedback = document?.getElementById("camera-feedback");
    if (cameraFeedback) {
      const drawContext = cameraFeedback?.getContext("2d");
      let color = "#FFFF00FF";

      if (displayable.detectionStatus === 0) {
        color = "#FF0000FF";
      } else if (displayable.detectionStatus === 1) {
        color = "#00FF00FF";
      }

      drawContext.fillStyle = color;
      drawContext.strokeStyle = color;
      drawContext.lineWidth = 5;
    }
  }

  function setupMessage(displayable) {
    switch (displayable.detectionStatus) {
      case BlinkIDSDK.DetectionStatus.Fail:
        updateScanFeedback(locale.t("kyc_scanning"));
        break;
      case BlinkIDSDK.DetectionStatus.Success:
      case BlinkIDSDK.DetectionStatus.FallbackSuccess:
        updateScanFeedback(locale.t("kyc_detected"));
        break;
      case BlinkIDSDK.DetectionStatus.CameraAtAngle:
        updateScanFeedback(locale.t("kyc_angle"));
        break;
      case BlinkIDSDK.DetectionStatus.CameraTooHigh:
        updateScanFeedback(locale.t("kyc_move"));
        break;
      case BlinkIDSDK.DetectionStatus.CameraTooNear:
      case BlinkIDSDK.DetectionStatus.DocumentTooCloseToEdge:
      case BlinkIDSDK.DetectionStatus.Partial:
        updateScanFeedback(locale.t("kyc_move_far"));
        break;
      default:
        console.warn(
          "Unhandled detection status!",
          displayable.detectionStatus
        );
    }
  }

  let scanFeedbackLock = false;

  /**
   * The purpose of this function is to ensure that scan feedback message is
   * visible for at least 1 second.
   */
  function updateScanFeedback(message, force) {
    const scanFeedback = document?.getElementById("camera-guides");
    if (scanFeedbackLock && !force) {
      return;
    }

    scanFeedbackLock = true;
    if (scanFeedback) {
      scanFeedback.innerText = message;
    }
    window.setTimeout(() => (scanFeedbackLock = false), 1000);
  }
  async function startScanFile(sdk, fileList) {
    const scanImageElement = document?.getElementById("target-image");
    const inputImageFile = document?.getElementById("image-file");
    document.getElementById("screen-start")?.classList.add("hidden");
    document.getElementById("screen-scanning")?.classList.remove("hidden");

    // 1. Create a recognizer objects which will be used to recognize single image or stream of images.
    //
    // BlinkID Single-side Recognizer - scan various ID documents
    const singleSideIDRecognizer =
      await BlinkIDSDK.createBlinkIdSingleSideRecognizer(sdk);
    const setting = await singleSideIDRecognizer.currentSettings();
    setting.returnEncodedFullDocumentImage = true;
    setting.anonymizationMode = 0;
    setting.fullDocumentImageDpi = 400;
    await singleSideIDRecognizer.updateSettings(setting);
    // 2. Create a RecognizerRunner object which orchestrates the recognition with one or more
    //    recognizer objects.
    const recognizerRunner = await BlinkIDSDK.createRecognizerRunner(
      // SDK instance to use
      sdk,
      // List of recognizer objects that will be associated with created RecognizerRunner object
      [singleSideIDRecognizer],
      // [OPTIONAL] Should recognition pipeline stop as soon as first recognizer in chain finished recognition
      false
    );

    // 3. Prepare image for scan action - keep in mind that SDK can only process images represented in
    //    internal CapturedFrame data structure. Therefore, auxiliary method "captureFrame" is provided.

    // Make sure that image file is provided
    let file = null;
    const imageRegex = RegExp(/^image\//);
    for (let i = 0; i < fileList.length; ++i) {
      if (imageRegex.exec(fileList[i].type)) {
        file = fileList[i];
      }
    }

    if (!file || file.type === "image/heic") {
      message.error(locale.t("error_verify_type_file"));

      // alert("No image files provided!");
      // Release memory on WebAssembly heap used by the RecognizerRunner
      recognizerRunner?.delete();

      // Release memory on WebAssembly heap used by the recognizer
      singleSideIDRecognizer?.delete();
      inputImageFile.value = "";
      scanImageElement.src = "";
      document.getElementById("screen-start")?.classList.remove("hidden");
      document.getElementById("screen-scanning")?.classList.add("hidden");
      initmicroblink();
      return;
    }
    const imageElement = new Image();
    const url = URL.createObjectURL(file);

    imageElement.src = url;
    scanImageElement.src = url;

    await imageElement.decode();
    const imageFrame = BlinkIDSDK.captureFrame(imageElement);
    URL.revokeObjectURL(url);

    // 4. Start the recognition and await for the results
    const processResult = await recognizerRunner.processImage(imageFrame);

    // 5. If recognition was successful, obtain the result and display it

    if (processResult !== BlinkIDSDK.RecognizerResultState.Empty) {
      const singleSideIDResults = await singleSideIDRecognizer.getResult();

      if (
        singleSideIDResults.state !== BlinkIDSDK.RecognizerResultState.Empty
      ) {
        const firstName =
          singleSideIDResults.firstName.latin ||
          singleSideIDResults.firstName.cyrillic ||
          singleSideIDResults.firstName.arabic;

        const lastName =
          singleSideIDResults.lastName.latin ||
          singleSideIDResults.lastName.cyrillic ||
          singleSideIDResults.lastName.arabic;

        const dateOfBirth = {
          year:
            singleSideIDResults.dateOfBirth.year ||
            singleSideIDResults.mrz.dateOfBirth.year,
          month:
            singleSideIDResults.dateOfBirth.month ||
            singleSideIDResults.mrz.dateOfBirth.month,
          day:
            singleSideIDResults.dateOfBirth.day ||
            singleSideIDResults.mrz.dateOfBirth.day,
        };
        const dateOfExpiry = {
          year:
            singleSideIDResults.dateOfExpiry.year ||
            singleSideIDResults.mrz.dateOfExpiry.year,
          month:
            singleSideIDResults.dateOfExpiry.month ||
            singleSideIDResults.mrz.dateOfExpiry.month,
          day:
            singleSideIDResults.dateOfExpiry.day ||
            singleSideIDResults.mrz.dateOfExpiry.day,
        };
        const countryName = singleSideIDResults.classInfo.isoAlpha2CountryCode;
        const documentType = singleSideIDResults.classInfo.documentType;

        const fullName =
          singleSideIDResults.fullName.latin ||
          singleSideIDResults.fullName.cyrillic ||
          singleSideIDResults.fullName.arabic ||
          lastName ||
          `${singleSideIDResults.mrz.secondaryID} ${singleSideIDResults.mrz.primaryID}`;

        const derivedFullName = `${firstName} ${lastName}`.trim() || fullName;

        const binaryString = Array.from(
          singleSideIDResults.fullDocumentImage.encodedImage
        )
          .map((byte) => String.fromCharCode(byte))
          .join("");
        const base64Strings = `${btoa(binaryString)}`;

        setImageSrc(base64Strings);
        onKycSuccess({
          first_name:
            firstName?.replace(/\n/g, " ") ||
            fullName?.replace(/\n/g, " ") ||
            derivedFullName?.replace(/\n/g, " "),
          last_name:
            firstName === "" ? "" : lastName?.replace(/\n/g, " ") || "",
          id_card_number:
            singleSideIDResults.documentAdditionalNumber.latin ||
            singleSideIDResults.documentNumber.latin ||
            singleSideIDResults.personalIdNumber.latin,
          birth_date: `${dateOfBirth.year}-${dateOfBirth.month}-${dateOfBirth.day}`,
          dateOfExpiry: `${dateOfExpiry.year}-${dateOfExpiry.month}-${dateOfExpiry.day}`,
          countryName: countryName,
          documentType: documentType,
          file: base64Strings,
        });
        // alert(
        //   `Hello, ${derivedFullName}!\n You were born on ${dateOfBirth.year}-${dateOfBirth.month}-${dateOfBirth.day}. id card ${singleSideIDResults.documentAdditionalNumber.latin}`
        // );
      }
    } else {
      message.error(locale.t("error_verify_kyc"));
      initmicroblink();
      history.push(`/${localStorage.getItem("locale")}/wallet/account`);
      // alert("Could not extract information!");
    }

    // 7. Release all resources allocated on the WebAssembly heap and associated with camera stream

    // Release memory on WebAssembly heap used by the RecognizerRunner
    recognizerRunner?.delete();

    // Release memory on WebAssembly heap used by the recognizer
    singleSideIDRecognizer?.delete();

    // Hide scanning screen and show scan button again
    inputImageFile.value = "";
    scanImageElement.src = "";
    document.getElementById("screen-start")?.classList.remove("hidden");
    document.getElementById("screen-scanning")?.classList.add("hidden");
  }
  // Run
  // main();
  const onChangeModal = (value) => {
    setIsAnother(value);
  };
  const uploadOption = {
    beforeUpload: async (file) => {
      const isPNG = file.type === "image/png";
      const isJPG = file.type === "image/jpg";
      const isJPEG = file.type === "image/jpeg";

      const isLt2M = file.size / 1024 / 1024 < 8;
      if (!isPNG && !isJPG && !isJPEG) {
        // message.error(`${file.name} is not an acceptable file type.`);
      } else if (!isLt2M) {
        message.error("Your file size exceeds 8MB");
      }

      return false;
    },
  };
  return (
    <div>
      {" "}
      {!isAnother ? (
        <>
          <div className="text-center text-main-black">
            <h1 className="fs-20 fw-300">{locale.t("verifyidentity")}</h1>
            <p className="fs-12 mt-3 fw-300">{locale.t("acceptedfile")}</p>
          </div>
          {/* <img src={imageSrc} alt={`Image`} /> */}
          <div className="d-flex justify-content-center mt-5">
            <div id="screen-initial">
              <h1 id="msg">Loading...</h1>
              <progress id="load-progress" value="0" max="100"></progress>
            </div>

            <div
              id="screen-start"
              className="hidden d-flex justify-content-center"
            >
              <div className="mr-5 text-center d-flex flex-column align-items-center">
                <div id="start-scan" className="iux-scan-id cursor-pointer">
                  <img src="/assets/images/verify/camera.svg" alt="camera" />
                </div>
                <div className="mt-4 text-gray">{locale.t("cameradevice")}</div>
              </div>
              <div className="d-flex flex-column align-items-center">
                <Upload
                  {...uploadOption}
                  id="image-file"
                  className=" cursor-pointer"
                  showUploadList={false}
                >
                  <div className="iux-scan-id">
                    <img
                      src="/assets/images/verify/uploadfile.svg"
                      alt="uploadfile"
                      width={32}
                    />
                  </div>
                </Upload>
                <div className="mt-4 text-gray"> {locale.t("uploadfile")}</div>
              </div>
              {/* <input
          id="image-file"
          type="file"
          accept="image/*"
          capture="environment"
        />
        <label for="image-file">Scan from file</label> */}
            </div>
            <div id="screen-scanning" className="hidden">
              <video
                id="camera-feed"
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  transform: "scale(-1, 1) !important",
                }}
              ></video>

              <canvas id="camera-feedback"></canvas>
              <p id="camera-guides">
                Point the camera towards front side of a document.
              </p>
            </div>
            <div id="screen-scanning" className="hidden">
              <h1>Processing...</h1>

              <img id="target-image" />
            </div>
          </div>
          <hr className="my-5" />
          <div className="mb-5">
            <div className="text-center"> {locale.t("or")}</div>
            <div
              className="text-center mt-3 text-primary-sub-iux cursor-pointer"
              onClick={() => onChangeModal(true)}
            >
              {locale.t("anotherdevice")}
            </div>
            <div className="text-center mt-3 text-lightdark ">
              {locale.t("anotherdeviceDetail")}
            </div>
            <div className="text-center mt-3 text-primary-sub-iux cursor-pointer">
              <Tutorial isButton />
            </div>
            {/* <div className="text-center mt-3">
              <Button
                className="btn-primary-new-iux ml-1 button-default-w "
                disabled
              >
                Continue
              </Button>
            </div>
            <div className="text-center mt-3 text-primary-sub-iux cursor-pointer">
              {" "}
              {locale.t("back")}
            </div> */}
          </div>{" "}
        </>
      ) : (
        <div className="d-flex justify-content-center flex-column align-items-center">
          <img
            src="/assets/images/qrcode/dynamiclink-iux-markets.png"
            alt="anotherdevice"
            width={200}
          />
          <p className="fs-14 text-lightgray mt-4">{locale.t("scancode")}</p>
          <p
            className="fs-14 text-lightgray mt-4 cursor-pointer text-primary-sub-iux "
            onClick={() => onChangeModal(false)}
          >
            {locale.t("back")}
          </p>
        </div>
      )}
    </div>
  );
}

export default Microblink;
